import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow-sm rounded-lg p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
            <p className="text-sm text-gray-600">Last updated: July 28, 2025</p>
          </div>

          <div className="prose prose-lg max-w-none">
            <h2>1. Introduction</h2>
            <p>
              Workazon ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy 
              explains how we collect, use, disclose, and safeguard your information when you use our 
              educational and career platform.
            </p>

            <h2>2. Information We Collect</h2>
            
            <h3>2.1 Personal Information</h3>
            <p>We may collect the following personal information:</p>
            <ul>
              <li><strong>Account Information:</strong> Name, email address, password, phone number</li>
              <li><strong>Profile Information:</strong> Educational background, work experience, skills, interests</li>
              <li><strong>Identity Verification:</strong> Government ID, social security number (for background checks)</li>
              <li><strong>Payment Information:</strong> Credit card details, billing address (processed securely)</li>
              <li><strong>Communication Data:</strong> Messages, support tickets, feedback</li>
            </ul>

            <h3>2.2 Educational and Assessment Data</h3>
            <ul>
              <li>Course progress and completion data</li>
              <li>Quiz and assessment responses</li>
              <li>Skill evaluation results</li>
              <li>AI-generated performance analytics</li>
              <li>Certification and achievement records</li>
            </ul>

            <h3>2.3 Employment-Related Information</h3>
            <ul>
              <li>Job application data and submissions</li>
              <li>Resume and portfolio content</li>
              <li>Employment preferences and career goals</li>
              <li>Interview schedules and feedback</li>
              <li>Background check results (when authorized)</li>
            </ul>

            <h3>2.4 Technical Information</h3>
            <ul>
              <li>IP address, browser type, and device information</li>
              <li>Usage data and analytics (pages visited, time spent)</li>
              <li>Cookies and similar tracking technologies</li>
              <li>Log files and error reports</li>
            </ul>

            <h2>3. How We Use Your Information</h2>
            
            <h3>3.1 Service Delivery</h3>
            <ul>
              <li>Provide and maintain our educational platform</li>
              <li>Process course enrollments and track progress</li>
              <li>Generate AI-powered skill assessments and recommendations</li>
              <li>Facilitate job matching and employer connections</li>
              <li>Process payments and manage subscriptions</li>
            </ul>

            <h3>3.2 Communication</h3>
            <ul>
              <li>Send course updates, notifications, and reminders</li>
              <li>Provide customer support and respond to inquiries</li>
              <li>Share job opportunities and career recommendations</li>
              <li>Send promotional content (with your consent)</li>
            </ul>

            <h3>3.3 Improvement and Analytics</h3>
            <ul>
              <li>Analyze usage patterns to improve our services</li>
              <li>Conduct research and development for new features</li>
              <li>Generate aggregated, non-identifiable statistics</li>
              <li>Enhance AI algorithms and assessment accuracy</li>
            </ul>

            <h2>4. Information Sharing and Disclosure</h2>
            
            <h3>4.1 With Employers</h3>
            <p>
              When you apply for jobs or opt into employer matching, we may share:
            </p>
            <ul>
              <li>Your profile information and resume</li>
              <li>Skill assessments and certification status</li>
              <li>Background check results (with your explicit consent)</li>
              <li>Application materials and responses</li>
            </ul>

            <h3>4.2 With Service Providers</h3>
            <p>We work with trusted third-party providers for:</p>
            <ul>
              <li>Payment processing (Stripe, PayPal)</li>
              <li>Background verification services</li>
              <li>Email delivery and communication</li>
              <li>Analytics and performance monitoring</li>
              <li>Cloud hosting and data storage</li>
            </ul>

            <h3>4.3 Legal Requirements</h3>
            <p>We may disclose information when required by law or to:</p>
            <ul>
              <li>Comply with legal processes and government requests</li>
              <li>Protect our rights, property, or safety</li>
              <li>Prevent fraud or security threats</li>
              <li>Enforce our Terms and Conditions</li>
            </ul>

            <h2>5. Data Security</h2>
            <p>
              We implement appropriate technical and organizational security measures to protect your 
              personal information against unauthorized access, alteration, disclosure, or destruction:
            </p>
            <ul>
              <li>Encryption of data in transit and at rest</li>
              <li>Regular security audits and vulnerability assessments</li>
              <li>Access controls and employee training</li>
              <li>Secure payment processing (PCI DSS compliant)</li>
              <li>Regular data backups and disaster recovery plans</li>
            </ul>

            <h2>6. Your Privacy Rights</h2>
            
            <h3>6.1 Access and Control</h3>
            <p>You have the right to:</p>
            <ul>
              <li>Access and review your personal information</li>
              <li>Update or correct inaccurate data</li>
              <li>Delete your account and associated data</li>
              <li>Export your data in a portable format</li>
              <li>Opt-out of marketing communications</li>
            </ul>

            <h3>6.2 California Residents (CCPA)</h3>
            <p>California residents have additional rights under the CCPA:</p>
            <ul>
              <li>Right to know what personal information is collected</li>
              <li>Right to delete personal information</li>
              <li>Right to opt-out of sale of personal information</li>
              <li>Right to non-discrimination for exercising privacy rights</li>
            </ul>

            <h3>6.3 European Users (GDPR)</h3>
            <p>EU residents have rights under GDPR including:</p>
            <ul>
              <li>Right of access, rectification, and erasure</li>
              <li>Right to restrict or object to processing</li>
              <li>Right to data portability</li>
              <li>Right to withdraw consent</li>
            </ul>

            <h2>7. Cookies and Tracking Technologies</h2>
            <p>
              We use cookies and similar technologies to enhance your experience:
            </p>
            <ul>
              <li><strong>Essential Cookies:</strong> Required for basic site functionality</li>
              <li><strong>Analytics Cookies:</strong> Help us understand how you use our service</li>
              <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
              <li><strong>Marketing Cookies:</strong> Deliver relevant advertisements (with consent)</li>
            </ul>
            <p>
              You can control cookie preferences through your browser settings or our cookie consent tool.
            </p>

            <h2>8. Third-Party Links and Services</h2>
            <p>
              Our platform may contain links to third-party websites or integrate with external services. 
              We are not responsible for the privacy practices of these third parties. We encourage you 
              to review their privacy policies before providing any information.
            </p>

            <h2>9. Children's Privacy</h2>
            <p>
              Our service is not intended for children under 13 years of age. We do not knowingly collect 
              personal information from children under 13. If we discover that we have collected information 
              from a child under 13, we will delete it immediately.
            </p>

            <h2>10. International Data Transfers</h2>
            <p>
              Your information may be processed and stored in countries other than your own. We ensure 
              appropriate safeguards are in place for international transfers, including:
            </p>
            <ul>
              <li>Standard contractual clauses approved by the European Commission</li>
              <li>Adequacy decisions for certain countries</li>
              <li>Certification schemes and codes of conduct</li>
            </ul>

            <h2>11. Data Retention</h2>
            <p>
              We retain your personal information for as long as necessary to provide our services and 
              comply with legal obligations:
            </p>
            <ul>
              <li>Account information: Until account deletion plus 30 days</li>
              <li>Educational records: 7 years for certification purposes</li>
              <li>Employment data: 3 years after last interaction</li>
              <li>Payment records: As required by tax and financial regulations</li>
              <li>Support communications: 2 years for quality assurance</li>
            </ul>

            <h2>12. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy periodically to reflect changes in our practices or 
              applicable laws. We will notify you of significant changes via email or through our 
              platform. The updated policy will be effective when posted.
            </p>

            <h2>13. Contact Us</h2>
            <p>
              If you have questions about this Privacy Policy or want to exercise your privacy rights, 
              please contact us:
            </p>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p><strong>Email:</strong> privacy@workazon.com</p>
              <p><strong>Data Protection Officer:</strong> dpo@workazon.com</p>
              <p><strong>Address:</strong> Workazon Privacy Team</p>
              <p className="text-sm text-gray-600 mt-2">
                For general support, please visit our <Link to="/support" className="text-primary-600 hover:text-primary-700">Support Page</Link>
              </p>
            </div>

            <h2>14. Consent</h2>
            <p>
              By using our service, you consent to the collection and use of your information as 
              described in this Privacy Policy. For certain activities like background checks or 
              data sharing with employers, we will obtain additional explicit consent.
            </p>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200 text-center">
            <Link 
              to="/" 
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
            >
              Return to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;